import MarkdownIt from "markdown-it";
import mdContainer from "markdown-it-container";
import katex from "katex";
import { callouts } from "./containers";
import { resolveFileRef, isHiddenRef, displayImageUrl } from "./fileref";
import { hashSource } from "./tikz";

/**
 * Zentrale markdown-it-Konfiguration inkl. eigener Erweiterungen:
 * Callout-Container, aufklappbar, karteikarte, tikz, KaTeX-Mathe ($…$/$$…$$),
 * Inline-Spoiler ||…||, echtes Inhaltsverzeichnis [toc], Tabellen.
 */

let instance: MarkdownIt | null = null;

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[äöü]/g, (m) => ({ ä: "ae", ö: "oe", ü: "ue" })[m] ?? m)
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] ?? c
  );
}

/**
 * `data-line`-Attribut für den Sync-Scroll (siehe `line_anchors`-Regel unten
 * und `features/editor/syncScroll.ts`): eigene Container rendern ihr HTML von
 * Hand statt über `renderToken`, darum hier per Hilfsfunktion einbauen statt
 * über `attrSet`.
 */
function lineAttr(t: { map?: [number, number] | null }): string {
  return t.map ? ` data-line="${t.map[0]}" data-line-end="${t.map[1]}"` : "";
}

/**
 * Diagrammarten, die Mermaid selbst als erste Zeile der Quelle erwartet, die
 * man aber natürlicher direkt als Zaunsprache schreibt (```xychart-beta).
 * Das `-beta` ist dabei freigestellt.
 */
const MERMAID_KINDS: Record<string, string> = {
  xychart: "xychart-beta",
  "xychart-beta": "xychart-beta",
  sankey: "sankey-beta",
  "sankey-beta": "sankey-beta",
  block: "block-beta",
  "block-beta": "block-beta",
  packet: "packet-beta",
  "packet-beta": "packet-beta",
  architecture: "architecture-beta",
  "architecture-beta": "architecture-beta",
  radar: "radar-beta",
  "radar-beta": "radar-beta",
  treemap: "treemap-beta",
  "treemap-beta": "treemap-beta",
};

/**
 * Ist diese Zaunsprache ein Mermaid-Diagramm?
 *   `null`  – nein
 *   `""`    – ja, klassisch (```mermaid), Quelle bleibt unangetastet
 *   sonst   – ja, und dieser Diagrammname muss der Quelle vorangestellt werden
 */
function mermaidLangOf(lang: string): string | null {
  if (lang === "mermaid") return "";
  return MERMAID_KINDS[lang] ?? null;
}

function getMarkdown(): MarkdownIt {
  if (instance) return instance;

  const md = new MarkdownIt({
    html: false,
    linkify: true,
    breaks: true,
    typographer: true,
  });

  // Externe Links in neuem Tab.
  const defaultLinkOpen =
    md.renderer.rules.link_open ||
    ((t, i, o, _e, s) => s.renderToken(t, i, o));
  // Begriffsdefinition/Fußnote: `[Begriff](def: "Erklärung")` – kein echter
  // Link, sondern ein Begriff, dessen Erklärung per Klick/Tippen als eigenes
  // Popup-Fenster aufgeht (siehe `bindTermPopups` in markdown/preview.ts –
  // der Markdown-Layer selbst rendert nur die Hülle mit `data-def`, kein
  // eigenes JS im HTML-String). Die Erklärung steht im Titel-Teil des Links,
  // nicht im Ziel selbst – Linkziele dürfen laut CommonMark keine rohen
  // Leerzeichen enthalten, ein Satz als Titel dagegen schon.
  let inTermLink = false;
  md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
    const href = tokens[idx].attrGet("href") || "";
    if (href === "def:" || href.startsWith("def:")) {
      inTermLink = true;
      const def = tokens[idx].attrGet("title") || "";
      return `<span class="md-term" tabindex="0" data-def="${esc(def)}">`;
    }
    // Verweis auf eine Seite im Arbeits-PDF: bleibt als Link stehen, wird
    // aber als Knopf gezeichnet und von der Editor-Seite abgefangen.
    if (href.startsWith("pdf:")) {
      tokens[idx].attrJoin("class", "md-pdf-link");
      return defaultLinkOpen(tokens, idx, options, env, self);
    }
    if (/^https?:\/\//.test(href)) {
      tokens[idx].attrSet("target", "_blank");
      tokens[idx].attrSet("rel", "noopener noreferrer");
    }
    tokens[idx].attrSet("href", resolveFileRef(href));
    return defaultLinkOpen(tokens, idx, options, env, self);
  };
  const defaultLinkClose =
    md.renderer.rules.link_close ||
    ((t, i, o, _e, s) => s.renderToken(t, i, o));
  md.renderer.rules.link_close = (tokens, idx, options, env, self) => {
    if (inTermLink) {
      inTermLink = false;
      return "</span>";
    }
    return defaultLinkClose(tokens, idx, options, env, self);
  };

  // Kurze Datei-Referenzen (`pb:<id>/<datei>`) erst beim Rendern auflösen.
  const defaultImage =
    md.renderer.rules.image || ((t, i, o, _e, s) => s.renderToken(t, i, o));
  md.renderer.rules.image = (tokens, idx, options, env, self) => {
    const src = tokens[idx].attrGet("src") || "";
    // Per Auge-Symbol im Editor ausgeblendet (z. B. ein Tafelbild-Foto, das
    // nur für einen selbst gedacht ist) – in Vorschau/Export/Hausaufgaben
    // erscheint an der Stelle nichts, im Quelltext bleibt das Bild stehen.
    if (isHiddenRef(src)) return "";
    tokens[idx].attrSet("src", displayImageUrl(resolveFileRef(src)));
    tokens[idx].attrSet("decoding", "async");
    tokens[idx].attrSet("loading", "lazy");
    return defaultImage(tokens, idx, options, env, self);
  };

  // Tabellen in einen eigenen Scrollbereich hüllen: auf schmalen Bildschirmen
  // wird geschoben statt gequetscht.
  md.renderer.rules.table_open = (tokens, idx) =>
    `<div class="md-table-scroll"${lineAttr(tokens[idx])}><table>\n`;
  md.renderer.rules.table_close = () => "</table></div>\n";

  // Sprach-Etikett sofort ins Markup schreiben (nicht erst nach dem
  // asynchronen Einfärben) – sonst flackert die Anzeige bei jedem Tastendruck.
  // Mermaid-Blöcke bekommen zusätzlich einen Quelltext-Hash: damit lässt sich
  // ein unveränderter Block über mehrere Re-Renders hinweg wiedererkennen
  // (siehe markdown/reuse.ts) statt jedes Mal neu gezeichnet zu werden.
  const defaultFence =
    md.renderer.rules.fence || ((t, i, o, _e, s) => s.renderToken(t, i, o));
  md.renderer.rules.fence = (tokens, idx, options, env, self) => {
    const html = defaultFence(tokens, idx, options, env, self);
    const lang = (tokens[idx].info || "").trim().split(/\s+/, 1)[0];
    const line = lineAttr(tokens[idx]);
    const mermaidKind = mermaidLangOf(lang);
    if (mermaidKind !== null) {
      // Diagrammarten wie `xychart-beta` oder `sankey-beta` dürfen auch direkt
      // als Zaunsprache stehen. Sie werden auf `language-mermaid` normiert,
      // damit der Zeichner sie findet; welche Art gemeint war, merkt sich
      // `data-mermaid-kind` – Mermaid erwartet den Namen als erste Zeile der
      // Quelle und bekommt ihn dort bei Bedarf vorangestellt (siehe mermaid.ts).
      const kindAttr = mermaidKind ? ` data-mermaid-kind="${esc(mermaidKind)}"` : "";
      return html
        .replace(
          /^<pre>/,
          `<pre data-src-hash="${hashSource(tokens[idx].content)}"${kindAttr}${line}>`
        )
        .replace(/<code class="language-[\w-]+"/, '<code class="language-mermaid"');
    }
    if (!lang || lang === "tikz") return html.replace(/^<pre>/, `<pre${line}>`);
    return html.replace(/^<pre>/, `<pre data-lang="${esc(lang.toLowerCase())}"${line}>`);
  };

  type Tk = { info: string; nesting: number; map?: [number, number] | null };

  // Callout-Boxen (info/warning/example/definition/summary) – Pfeil in der
  // Titelzeile klappt den Inhalt zu (siehe .md-fold-toggle in v1.css).
  for (const def of callouts) {
    md.use(mdContainer, def.name, {
      validate: (p: string) => p.trim().split(/\s+/, 1)[0] === def.name,
      render(tokens: Tk[], idx: number) {
        const t = tokens[idx];
        if (t.nesting === 1) {
          const title = t.info.trim().slice(def.name.length).trim() || def.title;
          return (
            `<div class="md-callout md-${def.name}"${lineAttr(t)}>` +
            `<div class="md-callout-title">${esc(title)}</div>` +
            `<div class="md-callout-body">\n`
          );
        }
        return `</div></div>\n`;
      },
    });
  }

  // Aufklappbar (+ Alias collapse).
  for (const name of ["aufklappbar", "collapse"]) {
    md.use(mdContainer, name, {
      validate: (p: string) => p.trim().split(/\s+/, 1)[0] === name,
      render(tokens: Tk[], idx: number) {
        const t = tokens[idx];
        if (t.nesting === 1) {
          const title = t.info.trim().slice(name.length).trim() || "Mehr anzeigen";
          return (
            `<details class="md-collapse"${lineAttr(t)}><summary>${esc(title)}</summary>` +
            `<div class="md-collapse-body">\n`
          );
        }
        return `</div></details>\n`;
      },
    });
  }

  // Karteikarte: ::: karteikarte Titel \n Vorderseite \n --- \n Rückseite :::
  // Interaktiv: Klick auf "Umdrehen"-Button zeigt Rückseite.
  md.use(mdContainer, "karteikarte", {
    validate: (p: string) => p.trim().split(/\s+/, 1)[0] === "karteikarte",
    render(tokens: Tk[], idx: number) {
      const t = tokens[idx];
      if (t.nesting === 1) {
        const title = t.info.trim().slice("karteikarte".length).trim() || "Karteikarte";
        return (
          `<div class="md-flashcard" data-fc="1"${lineAttr(t)}>` +
          `<div class="md-flashcard-header">` +
          `<span class="md-flashcard-title">${esc(title)}</span>` +
          `<span class="md-fc-side-label">Vorderseite</span>` +
          `</div>` +
          `<div class="md-flashcard-body">\n`
        );
      }
      return `</div>MD_FC_END</div>\n`;
    },
  });

  // Zitat: ::: zitat [Autor] … ::: – Container statt `>`-Blockquote (siehe
  // slash.ts/SNIPPETS.zitat für den Grund: markdown-it zieht bei `>` eine
  // Autorenzeile ohne `>` sonst per "lazy continuation" mit ins Zitat, wenn
  // keine Leerzeile davor steht). Der Autor steht wie bei den anderen
  // Containern (info/warning/…) direkt in der Kopfzeile, nicht im Text.
  md.use(mdContainer, "zitat", {
    validate: (p: string) => p.trim().split(/\s+/, 1)[0] === "zitat",
    render(tokens: Tk[], idx: number) {
      const t = tokens[idx];
      if (t.nesting === 1) {
        const author = t.info.trim().slice("zitat".length).trim();
        return (
          `<blockquote class="md-zitat"${lineAttr(t)}>` +
          (author ? `<div class="md-zitat-author">${esc(author)}</div>` : "") +
          `<div class="md-zitat-body">\n`
        );
      }
      return `</div></blockquote>\n`;
    },
  });

  // Hausaufgabe: ::: hausaufgabe … ::: – die Frist steht im Quelltext als
  // eigene „Frist:"-Zeile und wandert vor dem Rendern in die Info-Zeile
  // (siehe `liftHomeworkDue`), damit sie im Kopf der Box landet.
  md.use(mdContainer, "hausaufgabe", {
    validate: (p: string) => p.trim().split(/\s+/, 1)[0] === "hausaufgabe",
    render(tokens: Tk[], idx: number) {
      const t = tokens[idx];
      if (t.nesting !== 1) return `</div></div>\n`;
      const due = t.info.trim().slice("hausaufgabe".length).trim();
      return (
        `<div class="md-homework"${lineAttr(t)}>` +
        `<div class="md-homework-head">` +
        `<span class="md-homework-label">Hausaufgabe</span>` +
        (due ? `<span class="md-homework-due">${esc(due)}</span>` : "") +
        `</div>` +
        `<div class="md-homework-body">\n`
      );
    },
  });

  // Spalten-Layout: ::: spalten … ||| … ::: (||| trennt die Spalten).
  md.use(mdContainer, "spalten", {
    validate: (p: string) => p.trim().split(/\s+/, 1)[0] === "spalten",
    render(tokens: Tk[], idx: number) {
      const t = tokens[idx];
      return t.nesting === 1
        ? `<div class="md-cols"${lineAttr(t)}><div class="md-col">\n`
        : `</div></div>\n`;
    },
  });

  // Hinweis: `::: tikz … :::` wird NICHT als Container geparst, sondern in
  // `protect()` roh herausgeschnitten – sonst zerlegt markdown-it das LaTeX
  // (Backslashes, `_`, `$`). Gerendert wird es später von ./tikz.ts.

  // Aufgaben: jede Zeile, die mit „[ ]" oder „[x]" beginnt, wird zur
  // Checkbox – mit oder ohne Listenpunkt davor. Der `data-task`-Index zählt
  // in Dokumentreihenfolge und passt damit zum gleichnamigen Zähler in
  // `toggleTaskInSource` (siehe ./tasks.ts).
  /** Ein Tabulator (oder zwei Leerzeichen) je Stufe, höchstens vier. */
  const indentLevel = (line: string): number => {
    const indent = /^[ \t]*/.exec(line)?.[0] ?? "";
    if (!indent) return 0;
    const tabs = (indent.match(/\t/g) ?? []).length;
    const spaces = indent.length - tabs;
    return Math.min(4, tabs + Math.floor(spaces / 2));
  };

  md.core.ruler.push("task_lists", (state: { tokens: any[]; Token: any }) => {
    let n = 0;
    const raw = (content: string) => {
      const t = new state.Token("html_inline", "", 0);
      t.content = content;
      return t;
    };

    const toks = state.tokens;
    for (let i = 0; i < toks.length; i++) {
      const token = toks[i];
      if (token.type !== "inline" || !token.children?.length) continue;

      const out: any[] = [];
      let lineStart = true;
      let open = false;
      let firstIsTask = false;
      // Die Einrückung steht nur noch im Quelltext des Absatzes: der
      // Inline-Parser wirft führende Leerzeichen aus den Text-Tokens heraus.
      // Deshalb je Zeile dort nachsehen, wie tief sie eingerückt ist.
      const lines: string[] = (token.content ?? "").split("\n");
      let lineIndex = 0;

      for (const child of token.children) {
        if (child.type === "softbreak" || child.type === "hardbreak") {
          if (open) {
            out.push(raw("</span>"));
            open = false;
          }
          out.push(child);
          lineStart = true;
          lineIndex++;
          continue;
        }
        if (lineStart) {
          lineStart = false;
          const m = child.type === "text" ? /^\[([ xX])\][ \t]*/.exec(child.content) : null;
          if (m) {
            const done = m[1] !== " ";
            const level = indentLevel(lines[lineIndex] ?? "");
            child.content = child.content.slice(m[0].length);
            if (!out.length) firstIsTask = true;
            out.push(
              raw(
                `<span class="md-task-line${done ? " is-done" : ""}${level ? ` md-task-in${level}` : ""}">`
              )
            );
            out.push(
              raw(`<input class="md-task" type="checkbox" data-task="${n++}"${done ? " checked" : ""}>`)
            );
            open = true;
          }
        }
        out.push(child);
      }
      if (open) out.push(raw("</span>"));
      if (out.length === token.children.length) continue;
      token.children = out;

      // Steht die Aufgabe in einem Listenpunkt, entfällt dessen Aufzählungs-
      // zeichen – sonst stünden Punkt und Haken nebeneinander.
      if (firstIsTask && toks[i - 1]?.type === "paragraph_open" && toks[i - 2]?.type === "list_item_open") {
        toks[i - 2].attrJoin("class", "md-task-item");
      }
    }
  });

  // Block-Inhalte in Tabellenzellen (Codefelder, Callout-Boxen wie
  // „tafelbild", mehrere Absätze): eine Zelle ist in der GFM-Tabellensyntax
  // immer genau eine Quelltextzeile – Fences/Container brauchen aber echte
  // Zeilenumbrüche. `<br>` steht deshalb als Umbruch-Zeichen zur Verfügung:
  // NUR wenn eine Zelle das enthält, läuft ihr Inhalt (nach `<br>` -> echtem
  // Zeilenumbruch) noch einmal komplett durch die Block-Pipeline. Der
  // Normalfall – reiner Inline-Inhalt: fett, Links, Bilder, Formeln,
  // Codespans, Spoiler – bleibt beim schnellen Standardweg unangetastet, der
  // läuft schon lange und soll sich nicht ändern.
  const CELL_BREAK = /<br\s*\/?>/i;
  md.core.ruler.before("inline", "table_cell_blocks", (state: { tokens: any[] }) => {
    const toks = state.tokens;
    for (let i = 0; i < toks.length - 1; i++) {
      const isCellOpen = toks[i].type === "td_open" || toks[i].type === "th_open";
      const cell = toks[i + 1];
      if (!isCellOpen || cell.type !== "inline" || !CELL_BREAK.test(cell.content)) continue;
      const nested = cell.content.replace(/<br\s*\/?>/gi, "\n");
      // `html_block` ist ein eingebauter Token-Typ: der Standard-Renderer
      // gibt dessen `content` unverändert als HTML aus (siehe
      // Renderer.render in markdown-it) – genau die Lücke, die hier gebraucht
      // wird, um fertig gerendertes Block-HTML an Stelle einer Inline-Zelle
      // einzusetzen.
      cell.type = "html_block";
      // Zeilenanker zählen hier ab Zellenanfang statt ab Dokumentanfang –
      // Falten und Mitscrollen sprängen sonst an eine völlig falsche Stelle.
      cell.content = md.render(nested).replace(/ data-line(?:-end)?="\d+"/g, "");
      cell.children = null;
    }
  });

  // Überschriften-IDs für TOC-Anker.
  md.core.ruler.push("heading_ids", (state: { tokens: any[] }) => {
    const used: Record<string, number> = {};
    const toks = state.tokens;
    for (let i = 0; i < toks.length; i++) {
      if (toks[i].type !== "heading_open") continue;
      const inline = toks[i + 1];
      const text = inline && inline.type === "inline" ? inline.content : "";
      let slug = slugify(text) || "abschnitt";
      if (used[slug] != null) {
        used[slug] += 1;
        slug = `${slug}-${used[slug]}`;
      } else used[slug] = 0;
      toks[i].attrSet("id", slug);
    }
  });

  // Feinkörnige Sync-Scroll-Anker: jeder Block bekommt seine Quelltextzeile
  // als `data-line` (siehe features/editor/syncScroll.ts) – nicht nur
  // Überschriften, sonst driften Vorschau und Textfeld innerhalb eines
  // Abschnitts auseinander (lange Absätze, Bilder, Codeblöcke, Listen …).
  // Eigene Container (Callouts, Tabellen, Codeblöcke, …) tragen ihr
  // `data-line` von Hand, weil sie ihr HTML nicht über `renderToken`
  // erzeugen (siehe `lineAttr` oben).
  const LINE_ANCHOR_TYPES = new Set([
    "paragraph_open",
    "heading_open",
    "blockquote_open",
    "bullet_list_open",
    "ordered_list_open",
    "list_item_open",
    "hr",
    "html_block",
  ]);
  md.core.ruler.push("line_anchors", (state: { tokens: any[] }) => {
    for (const t of state.tokens) {
      if (!t.map || !LINE_ANCHOR_TYPES.has(t.type)) continue;
      t.attrSet("data-line", String(t.map[0]));
      // Auch das Ende: ein Block, der im Quelltext zwei Zeilen lang ist, in
      // der Vorschau aber ein großes Bild oder eine lange Tabelle ist, wird
      // damit als Strecke abgebildet statt als einzelner Punkt.
      t.attrSet("data-line-end", String(t.map[1]));
    }
  });

  instance = md;
  return md;
}

interface Heading {
  level: number;
  text: string;
  slug: string;
  /** 0-basierte Zeile im Quelltext (für Sync-Scroll). */
  line: number;
}

/**
 * Ist eine Überschrift NUR ein Link (z. B. ein per "PDF-Seite einfügen"
 * gesetzter Verweis als eigene Zeile), zählt im Inhaltsverzeichnis der
 * LINKTEXT, nicht die rohe `[Text](ziel)`-Syntax – sonst steht im TOC genau
 * das eckige-Klammern-Geraffel statt eines lesbaren Titels. Bei einem
 * PDF-Verweis fällt zusätzlich die technische ", S. 3"-Endung weg (die
 * gehört an die Fundstelle im Text, nicht ins kurze TOC-Label) und eine
 * Dateiendung im Namen (Material wurde als "Name.pdf" hochgeladen).
 */
function headingText(raw: string): string {
  const link = /^\[(.+)\]\([^)]*\)$/.exec(raw);
  if (!link) return raw;
  return link[1]
    .replace(/,\s*S\.\s*\d+$/i, "")
    .replace(/\.(pdf|docx?|pptx?|png|jpe?g|gif|webp)$/i, "")
    .trim();
}

export function collectHeadings(src: string): Heading[] {
  const used: Record<string, number> = {};
  const out: Heading[] = [];
  let inFence = false;
  const lines = src.split("\n");
  for (let ln = 0; ln < lines.length; ln++) {
    const line = lines[ln];
    if (/^\s*```/.test(line)) {
      inFence = !inFence;
      continue;
    }
    if (inFence) continue;
    const m = line.match(/^(#{1,3})\s+(.+?)\s*#*$/);
    if (!m) continue;
    const text = headingText(m[2].trim());
    let slug = slugify(text) || "abschnitt";
    if (used[slug] != null) {
      used[slug] += 1;
      slug = `${slug}-${used[slug]}`;
    } else used[slug] = 0;
    out.push({ level: m[1].length, text, slug, line: ln });
  }
  return out;
}

export function tocHtml(headings: Heading[], lineAttr = ""): string {
  if (!headings.length) {
    return `<nav class="md-toc"${lineAttr}><div class="md-toc-head">Inhalt</div><p class="md-toc-empty">Noch keine Überschriften</p></nav>`;
  }
  const counters = [0, 0, 0];
  const rows = headings
    .map((h) => {
      counters[h.level - 1] += 1;
      for (let i = h.level; i < 3; i++) counters[i] = 0;
      const num = counters.slice(0, h.level).join(".");
      return (
        `<a class="md-toc-item md-toc-l${h.level}" href="#${h.slug}">` +
        `<span class="md-toc-num">${num}</span>` +
        `<span class="md-toc-text">${esc(h.text)}</span></a>`
      );
    })
    .join("");
  return `<nav class="md-toc"${lineAttr}><div class="md-toc-head">Inhalt</div>${rows}</nav>`;
}

interface Protected {
  text: string;
  math: { latex: string; display: boolean }[];
  spoilers: string[];
  tikz: { type: string; source: string }[];
}

/**
 * Schneidet TikZ-Blöcke roh aus dem Quelltext – als `::: tikz … :::` oder als
 * ```` ```tikz ```` -Fence. Muss VOR dem Mathe-Schutz laufen, weil LaTeX
 * selbst `$` enthalten kann.
 */
function protectTikz(src: string, out: { type: string; source: string }[]): string {
  const lines = src.split("\n");
  const result: string[] = [];
  for (let i = 0; i < lines.length; i++) {
    const container = /^[ \t]*:::+[ \t]*(tikz|dbml)\b/.exec(lines[i]);
    const fence = /^[ \t]*```+[ \t]*(tikz|dbml)[ \t]*$/.exec(lines[i]);
    if (!container && !fence) {
      result.push(lines[i]);
      continue;
    }
    const closeRe = container ? /^[ \t]*:::+[ \t]*$/ : /^[ \t]*```+[ \t]*$/;
    const body: string[] = [];
    let j = i + 1;
    for (; j < lines.length && !closeRe.test(lines[j]); j++) body.push(lines[j]);
    result.push(`KTXTIKZ${out.length}KTX`);
    out.push({ type: (container ?? fence)![1], source: body.join("\n") });
    i = j; // Schlusszeile überspringen
  }
  return result.join("\n");
}

/**
 * Ersetzt den `---`-Trenner innerhalb von ::: karteikarte … ::: und
 * ::: zitat … :::-Blöcken durch einen Platzhalter (je Typ eigener). Sonst
 * macht markdown-it aus der Zeile davor eine Setext-Überschrift (h2) statt
 * eines <hr>, und Vorder-/Rückseite lassen sich nicht trennen. Verschachtelte
 * Container werden über eine Tiefen-Stack verfolgt.
 */
const SEP_PLACEHOLDERS: Record<string, string> = {
  karteikarte: "KTXFCSEPKTX",
};

function protectContainerSeparators(src: string): string {
  const lines = src.split("\n");
  const stack: string[] = []; // Namen der gerade offenen ::: Blöcke, außen -> innen
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const open = line.match(/^[ \t]*:::+\s*(\w+)/);
    const close = /^[ \t]*:::+\s*$/.test(line);
    if (open) {
      stack.push(open[1]);
      continue;
    }
    if (close) {
      stack.pop();
      continue;
    }
    const placeholder = SEP_PLACEHOLDERS[stack[stack.length - 1]];
    if (placeholder && /^[ \t]*-{3,}[ \t]*$/.test(line)) {
      lines[i] = placeholder;
    }
  }
  return lines.join("\n");
}

/**
 * Zieht in `::: hausaufgabe`-Blöcken die „Frist:"-Zeile in die Info-Zeile des
 * Containers hoch, wirft die interne `ID:`-Zeile weg und entfernt ein
 * einleitendes „Thema:"/„Aufgabe:"-Label.
 * Der Nutzer schreibt also die bequeme Langform, gerendert wird eine Box mit
 * Kopfzeile.
 */
export function liftHomeworkDue(src: string): string {
  const lines = src.split("\n");
  for (let i = 0; i < lines.length; i++) {
    if (!/^[ \t]*:::+[ \t]*hausaufgabe[ \t]*$/i.test(lines[i])) continue;
    // Verwaltungszeile `ID: …` (Verknüpfung zur angelegten Hausaufgabe, siehe
    // features/homework/fromNotes.ts) gehört nicht in die Anzeige.
    for (let j = i + 1; j < lines.length; j++) {
      if (/^[ \t]*:::/.test(lines[j])) break;
      if (/^[ \t]*ID[ \t]*:[ \t]*[A-Za-z0-9_-]+[ \t]*$/i.test(lines[j])) {
        lines.splice(j, 1);
        j--;
      }
    }
    for (let j = i + 1; j < lines.length; j++) {
      if (/^[ \t]*:::/.test(lines[j])) break;
      const due = /^[ \t]*Frist[ \t]*:[ \t]*(.*)$/i.exec(lines[j]);
      if (due) {
        lines[i] = `::: hausaufgabe ${due[1].trim()}`;
        lines.splice(j, 1);
        break;
      }
      if (lines[j].trim()) break; // erste echte Zeile ist keine Frist
    }
    // Führendes „Thema:"/„Aufgabe:" am Anfang des Rumpfs abräumen.
    for (let j = i + 1; j < lines.length; j++) {
      if (/^[ \t]*:::/.test(lines[j])) break;
      if (!lines[j].trim()) continue;
      lines[j] = lines[j].replace(/^[ \t]*(?:Thema|Aufgabe)[ \t]*:[ \t]*/i, "");
      break;
    }
  }
  return lines.join("\n");
}

/**
 * Ein echter Zeilenumbruch in `$$ … $$` soll auch einer sein, ohne dass
 * jedes Mal `\\` ans Zeilenende muss – KaTeX (wie LaTeX) behandelt einen
 * rohen Zeilenumbruch sonst nur als Leerzeichen. Steht am Zeilenende schon
 * ein manuelles `\\` (z. B. für zusätzlichen Abstand), wird nichts doppelt
 * eingefügt.
 *
 * Mehrzeiliges wird in eine Umgebung gepackt statt `\\` einfach lose zwischen
 * die Zeilen zu setzen: nacktes `\\` außerhalb einer Umgebung ist kein
 * gültiges LaTeX (KaTeX meldet `newLineInDisplayMode`). Ohne `&` wird es
 * `gathered` (jede Zeile zentriert) – `aligned` ohne `&` würde jede Zeile in
 * seine rechtsbündige erste Spalte stecken. Mit `&` (z. B. Umformungen, die
 * am `=` ausgerichtet sein sollen) ist `aligned` gewollt.
 * Hat der Nutzer schon selbst eine Umgebung (`\begin{...}`) geschrieben,
 * bleibt die unangetastet.
 */
function autoLineBreaks(latex: string): string {
  if (!latex.includes("\n") || /^\s*\\begin\{/.test(latex)) return latex;
  const lines = latex.split("\n");
  const body = lines
    .map((line, i) => (i < lines.length - 1 && !/\\\\\s*$/.test(line) ? line + " \\\\" : line))
    .join("\n");
  const env = /(^|[^\\])&/.test(latex) ? "aligned" : "gathered";
  return `\\begin{${env}}${body}\\end{${env}}`;
}

function protect(src: string): Protected {
  const math: { latex: string; display: boolean }[] = [];
  const spoilers: string[] = [];
  const tikz: { type: string; source: string }[] = [];
  let text = liftHomeworkDue(src);

  // TikZ zuerst: das LaTeX darin darf weder von markdown-it noch vom
  // Mathe-Schutz angefasst werden.
  text = protectTikz(text, tikz);

  // Karteikarten-Trenner schützen: `---` innerhalb von ::: karteikarte … :::
  // würde sonst die Zeile darüber zur Setext-Überschrift machen statt zu <hr>.
  text = protectContainerSeparators(text);

  // Formelzeile des Tabellen-Editors (features/editor/tableFormulas.ts) nicht
  // anzeigen. Als Leerzeile statt ganz weg: GFM würde sie sonst als weitere
  // Tabellenzeile lesen, und die Zeilennummern (Mitscrollen) bleiben gleich.
  text = text.replace(/^[ \t]*\[\/\/\]: #formeln=\S+[ \t]*$/gm, "");

  // [toc] -> Platzhalter
  text = text.replace(/^[ \t]*\[\[?toc\]?\][ \t]*$/gim, "KTXTOCKTX");

  // Display-Mathe $$ ... $$. Der Platzhalter behält die Zeilenzahl des
  // Originals (Blindzeilen statt einfach zu kürzen) – sonst verschieben sich
  // die `data-line`-Nummern (Sync-Scroll, Falten) für alles, was einer
  // mehrzeiligen Formel im Quelltext folgt. `restore()` entfernt die
  // Blindzeilen wieder (inkl. des `<br>`, das `breaks:true` sonst daraus macht).
  text = text.replace(/\$\$([\s\S]+?)\$\$/g, (m, latex) => {
    const i = math.length;
    math.push({ latex: autoLineBreaks(latex.trim()), display: true });
    const newlines = (m.match(/\n/g) || []).length;
    return `KTXMATH${i}KTX` + "\nKTXBLANKKTX".repeat(newlines);
  });
  // Inline-Mathe $ ... $
  text = text.replace(/\$([^$\n]+?)\$/g, (_m, latex) => {
    const i = math.length;
    math.push({ latex: latex.trim(), display: false });
    return `KTXMATH${i}KTX`;
  });
  // Spoiler ||text||
  text = text.replace(/\|\|([^|\n]+?)\|\|/g, (_m, content) => {
    const i = spoilers.length;
    spoilers.push(content);
    return `KTXSPOIL${i}KTX`;
  });

  return { text, math, spoilers, tikz };
}

/**
 * Deutsches Komma als Dezimaltrenner: KaTeX/LaTeX behandelt ein rohes `,`
 * sonst als Aufzählungstrenner (Extra-Abstand, „3, 14" statt „3,14"). `{,}`
 * ist der LaTeX-Standardweg dagegen.
 *
 * ponytail: einfache Ziffer-Komma-Ziffer-Erkennung – trifft auch ein Komma
 * in einem Koordinatenpaar wie "(3,4)" innerhalb von Mathe. Kommt in der
 * Praxis selten vor; bei Bedarf hier auf echte Dezimalstellen (max. 2-3
 * Nachkommaziffern) einschränken.
 */
function germanDecimals(latex: string): string {
  return latex.replace(/(\d),(\d)/g, "$1{,}$2");
}

/**
 * `3*4` als Malpunkt zeigen. Stehen lassen: `\section*`, `x^*`/`x_*` und
 * Umgebungsnamen wie `{align*}` – dort ist der Stern Teil des Befehls.
 */
function starsToCdot(latex: string): string {
  return latex.replace(/(\\[a-zA-Z]+|[\^_]|\{[a-zA-Z]*(?=\*\}))?\*/g, (m, keep) => (keep ? m : "\\cdot "));
}

/**
 * KaTeX ist beim Tippen der teuerste Teil des Renderns – und fast jede Formel
 * ist dieselbe wie beim letzten Tastendruck. Begrenzt, damit eine lange
 * Sitzung nicht unbegrenzt Speicher sammelt (Map hält die Einfügereihenfolge).
 */
const katexCache = new Map<string, string>();
const KATEX_CACHE_MAX = 3000;

function renderMath(latex: string, display: boolean): string {
  const key = (display ? "D" : "I") + latex;
  const hit = katexCache.get(key);
  if (hit !== undefined) return hit;
  let html: string;
  try {
    html = katex.renderToString(starsToCdot(germanDecimals(latex)), { displayMode: display, throwOnError: false });
  } catch {
    html = `<code class="md-math-error">${esc(latex)}</code>`;
  }
  if (katexCache.size >= KATEX_CACHE_MAX) katexCache.delete(katexCache.keys().next().value!);
  katexCache.set(key, html);
  return html;
}

function restore(html: string, p: Protected, headings: Heading[]): string {
  let out = html;
  // Läuft je Block (siehe renderMarkdownBlocks) – ein gewöhnlicher Absatz hat
  // keinen Platzhalter und braucht keine der Ersetzungen unten.
  if (!out.includes("KTX") && !out.includes("|||") && !out.includes("MD_FC_END")) return out;
  // Blindzeilen aus dem Zeilenzahl-Erhalt oben (siehe protect()) wieder raus –
  // inklusive des <br>, das `breaks:true` aus dem internen Zeilenumbruch macht.
  if (out.includes("KTXBLANKKTX")) out = out.replace(/(<br\s*\/?>\s*)?KTXBLANKKTX/g, "");
  // TikZ: nur die Hülle mit der Quelle; das SVG holt ./tikz.ts nach dem Einhängen.
  // Wie beim Inhaltsverzeichnis (siehe unten): der umschließende Absatz trägt
  // bereits `data-line` – das übernimmt die Hülle, sonst hätte ausgerechnet
  // der Block, der eine Quellzeile zu hundert Pixeln Vorschau aufbläst, gar
  // keinen Anker für den Mitscroll-Abgleich (features/editor/syncScroll.ts).
  if (out.includes("KTXTIKZ")) out = out.replace(
    /<p([^>]*)>\s*KTXTIKZ(\d+)KTX\s*<\/p>|KTXTIKZ(\d+)KTX/g,
    (_m, attrs: string | undefined, a, b) => {
      const { type, source } = p.tikz[Number(a ?? b)] ?? { type: "tikz", source: "" };
      const line = attrs ? (/ data-line="\d+"(?: data-line-end="\d+")?/.exec(attrs)?.[0] ?? "") : "";
      return (
        `<div class="md-tikz is-loading"${line} data-kroki-type="${type}" data-tikz="${encodeURIComponent(source)}">` +
        `<div class="md-tikz-label">${type === "dbml" ? "DBML" : "TikZ"} wird gerendert …</div></div>`
      );
    }
  );
  if (out.includes("KTXSPOIL")) out = out.replace(/KTXSPOIL(\d+)KTX/g, (_m, i) => {
    const content = p.spoilers[Number(i)] ?? "";
    return `<span class="md-spoiler" tabindex="0"><span class="md-spoiler-inner">${esc(content)}</span></span>`;
  });
  // Der Platzhalter steckt in einem <p>, das seine Quellzeile trägt. Ein <nav>
  // darf darin nicht stehen (der Parser würde es herausziehen), also ersetzt
  // das Verzeichnis den ganzen Absatz – und erbt dabei dessen `data-line`.
  // Ohne diesen Anker wüsste der Mitscroll-Abgleich nicht, wo das
  // Verzeichnis anfängt, und die Vorschau liefe gegenüber dem Text davor und
  // danach auseinander (siehe features/editor/syncScroll.ts).
  if (out.includes("KTXTOCKTX")) {
    out = out.replace(
      /<p([^>]*)>KTXTOCKTX<\/p>/g,
      (_m, attrs: string) => tocHtml(headings, / data-line="\d+"/.exec(attrs)?.[0] ?? "")
    );
    out = out.replace(/KTXTOCKTX/g, () => tocHtml(headings));
  }
  // Spaltentrenner ||| innerhalb von ::: spalten :::
  if (out.includes("|||")) out = out.replace(/<p>\|\|\|<\/p>/g, `</div><div class="md-col">`);
  // Interaktive Karteikarten: split am Trenner-Platzhalter, Flip-Button einfügen.
  // Der Platzhalter KTXFCSEPKTX (aus protectFlashcardSeparators) kann – je nach
  // breaks-Modus – als eigener <p>KTXFCSEPKTX</p> ODER mitten in einem Absatz
  // (…<br>\nKTXFCSEPKTX<br>\n…) landen. Wir behandeln beide Fälle.
  if (out.includes("MD_FC_END")) out = out.replace(
    /<div class="md-flashcard-body">([\s\S]*?)MD_FC_END<\/div>/g,
    (_m, body: string) => {
      const sepRe = /(?:<\/p>\s*)?<p>\s*KTXFCSEPKTX\s*<\/p>(?:\s*<p>)?|<br>\s*KTXFCSEPKTX<br>|<br>\s*KTXFCSEPKTX|KTXFCSEPKTX<br>|KTXFCSEPKTX/;
      const sep = body.match(sepRe);

      if (!sep || sep.index == null) {
        return `<div class="md-flashcard-body"><div class="md-fc-front">${body}</div>` +
          `<div class="md-fc-footer"><span class="md-fc-hint">Keine Rückseite</span></div></div>`;
      }

      let front = body.slice(0, sep.index);
      let back  = body.slice(sep.index + sep[0].length);

      // Falls der Trenner mitten im Absatz lag: offene Tags schließen/öffnen.
      front = balanceParagraph(front);
      back  = balanceParagraph(back, true);

      const onclick =
        `var fc=this.closest('[data-fc]');` +
        `fc.classList.toggle('is-flipped');` +
        `var lbl=fc.querySelector('.md-fc-side-label');` +
        `if(lbl)lbl.textContent=fc.classList.contains('is-flipped')?'Rückseite':'Vorderseite';` +
        `this.textContent=fc.classList.contains('is-flipped')?'Verbergen':'Umdrehen'`;
      return `<div class="md-flashcard-body">` +
        `<div class="md-fc-front">${front}</div>` +
        `<div class="md-fc-back">${back}</div>` +
        `<div class="md-fc-footer">` +
        `<button class="md-fc-btn" type="button" onclick="${onclick.replace(/"/g, '&quot;')}">Umdrehen</button>` +
        `</div>` +
        `</div>`;
    }
  );
  // Übrig gebliebene Trenner-Platzhalter (außerhalb von Karten) entfernen.
  if (out.includes("KTXFCSEPKTX")) out = out.replace(/<p>\s*KTXFCSEPKTX\s*<\/p>/g, "<hr>").replace(/KTXFCSEPKTX/g, "");
  // Formeln zuletzt: KaTeX-HTML ist lang, alle Ersetzungen oben müssten es
  // sonst mit durchsuchen. Nebenbei landen so auch Formeln in Spoilern
  // (deren Inhalt erst oben eingesetzt wird) gerendert statt als Platzhalter.
  if (out.includes("KTXMATH")) {
    out = out.replace(/KTXMATH(\d+)KTX/g, (_m, i) => {
      const item = p.math[Number(i)];
      return item ? renderMath(item.latex, item.display) : "";
    });
  }
  return out;
}

/**
 * Schließt einen unbalancierten führenden/abschließenden <p>/<br>, der durch
 * das Aufteilen eines Absatzes am Trenner entstehen kann.
 */
function balanceParagraph(html: string, opensParagraph = false): string {
  let s = html.trim().replace(/^<br>\s*/, "").replace(/\s*<br>$/, "");
  const opens  = (s.match(/<p>/g) || []).length;
  const closes = (s.match(/<\/p>/g) || []).length;
  if (opensParagraph && closes > opens) s = "<p>" + s;       // back: führendes <p> ergänzen
  if (!opensParagraph && opens > closes) s = s + "</p>";     // front: abschließendes </p> ergänzen
  return s;
}

export function renderMarkdown(src: string): string {
  return renderMarkdownBlocks(src).join("");
}

/**
 * Dasselbe HTML wie `renderMarkdown`, aber je Block der obersten Ebene ein
 * eigener String. Die Vorschau tauscht damit beim Tippen nur die Blöcke aus,
 * die sich wirklich geändert haben (markdown/reuse.ts) – ein Neuaufbau der
 * kompletten Vorschau kostete bei langen Mitschriften pro Tastendruck mehr
 * Layout-Zeit als alles andere zusammen.
 *
 * `restore` läuft je Block: keine seiner Ersetzungen reicht über einen Block
 * der obersten Ebene hinaus (Container wie Karteikarte/Spalten sind selbst
 * ein solcher Block).
 */
export function renderMarkdownBlocks(src: string): string[] {
  const source = src ?? "";
  const headings = collectHeadings(source);
  const p = protect(source);
  const md = getMarkdown();
  const env = {};
  const tokens = md.parse(p.text, env);
  const blocks: string[] = [];
  let start = 0;
  let depth = 0;
  for (let i = 0; i < tokens.length; i++) {
    depth += tokens[i].nesting;
    if (depth !== 0) continue;
    blocks.push(restore(md.renderer.render(tokens.slice(start, i + 1), md.options, env), p, headings));
    start = i + 1;
  }
  if (start < tokens.length) {
    blocks.push(restore(md.renderer.render(tokens.slice(start), md.options, env), p, headings));
  }
  return blocks;
}
